"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import {
  captureSnapshot,
  checkSnapshot,
} from "@/actions/tools/capture-snapshot";
import { Download, Loader2 } from "lucide-react";
import { DevicePreset } from "@/lib/viewports";
import { useTurnstileSession } from "@/hooks/use-turnstile-session";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ViewportSelector } from "@/components/ui/viewport-selector";
import { TurnstileChallenge } from "@/components/security/turnstile-challenge";
import { ImageModal } from "@/components/shared/image-modal";
import { ToolRegistrationCTA } from "@/components/tools/tool-registration-cta";
import { OutputFormat, ImageOptions } from "@watchero/types";

interface ScreenshotState {
  url: string;
  width: number;
  height: number;
  deviceScaleFactor: number;
  isMobile: boolean;
  hasTouch: boolean;
  format: OutputFormat;
  imageOptions: ImageOptions;
  snapshotId?: string;
  status?: string;
  screenshotUrl?: string;
  error?: string;
}

interface PollControl {
  token: number;
  timer?: ReturnType<typeof setTimeout>;
}

interface WebsiteScreenshotGeneratorProps {
  devicePresets: DevicePreset[];
  initialUrl?: string;
  initialViewport?: string;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120000;

export function WebsiteScreenshotGenerator({
  devicePresets,
  initialUrl,
  initialViewport,
}: WebsiteScreenshotGeneratorProps) {
  const getDefaultPreset = () => {
    if (initialViewport) {
      const found = devicePresets.find((p) => p.id === initialViewport);
      if (found) return found;
    }
    return devicePresets.find((p) => p.id === "macbook-pro-14");
  };

  const defaultPreset = getDefaultPreset();

  const [state, setState] = useState<ScreenshotState>({
    url: initialUrl ?? "",
    width: defaultPreset?.width ?? 1920,
    height: defaultPreset?.height ?? 1080,
    deviceScaleFactor: defaultPreset?.deviceScaleFactor ?? 2,
    isMobile: defaultPreset?.isMobile ?? false,
    hasTouch: defaultPreset?.hasTouch ?? false,
    format: "jpeg",
    imageOptions: {
      fullPage: true,
      quality: 100,
    },
  });
  const [selectedPresetId, setSelectedPresetId] = useState<string>(
    defaultPreset?.id ?? "",
  );
  const [viewportMode, setViewportMode] = useState<"preset" | "custom">(
    "preset",
  );
  const [isPending, startTransition] = useTransition();
  const [isPolling, setIsPolling] = useState(false);
  const pollControl = useRef<PollControl>({ token: 0 });
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  const { isReady: isTurnstileReady } = useTurnstileSession();

  const selectedPreset = devicePresets.find((p) => p.id === selectedPresetId);

  useEffect(() => {
    const control = pollControl.current;
    return () => {
      if (control.timer) {
        clearTimeout(control.timer);
      }
    };
  }, []);

  const clearPollingTimer = () => {
    if (pollControl.current.timer) {
      clearTimeout(pollControl.current.timer);
      pollControl.current.timer = undefined;
    }
  };

  const schedulePoll = (
    token: number,
    snapshotId: string,
    startedAt: number,
  ) => {
    pollControl.current.timer = setTimeout(async () => {
      if (pollControl.current.token !== token) {
        return;
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        pollControl.current.timer = undefined;
        setIsPolling(false);
        setState((previous) => ({
          ...previous,
          error: "Timed out waiting for screenshot",
          status: undefined,
          snapshotId: undefined,
          screenshotUrl: undefined,
        }));
        return;
      }

      try {
        const result = await checkSnapshot({
          snapshotId,
          timeout: POLL_INTERVAL_MS,
        });

        if (pollControl.current.token !== token) {
          return;
        }

        if (result.success) {
          setState((previous) => ({
            ...previous,
            snapshotId: result.snapshotId,
            status: result.status,
            screenshotUrl: result.screenshotUrl ?? previous.screenshotUrl,
            error: undefined,
          }));

          if (result.status === "COMPLETE") {
            pollControl.current.timer = undefined;
            setIsPolling(false);
            return;
          }

          schedulePoll(token, snapshotId, startedAt);
        } else {
          pollControl.current.timer = undefined;
          setIsPolling(false);
          setState((previous) => ({
            ...previous,
            error: result.error,
            status: undefined,
            snapshotId: undefined,
            screenshotUrl: undefined,
          }));
        }
      } catch (error) {
        if (pollControl.current.token !== token) {
          return;
        }

        pollControl.current.timer = undefined;
        setIsPolling(false);
        setState((previous) => ({
          ...previous,
          error: error instanceof Error ? error.message : "Unexpected error",
          status: undefined,
          snapshotId: undefined,
          screenshotUrl: undefined,
        }));
      }
    }, POLL_INTERVAL_MS);
  };

  const handleGenerate = () => {
    const url = state.url.trim();
    if (!url) {
      setState((previous) => ({
        ...previous,
        error: "Please provide a public URL.",
      }));
      return;
    }

    if (!isTurnstileReady) {
      setState((previous) => ({
        ...previous,
        error: "Complete the Turnstile challenge before generating.",
      }));
      return;
    }

    const nextToken = pollControl.current.token + 1;
    pollControl.current.token = nextToken;
    clearPollingTimer();
    setIsPolling(false);

    setState((previous) => ({
      ...previous,
      error: undefined,
      snapshotId: undefined,
      status: undefined,
      screenshotUrl: undefined,
    }));

    const viewport = {
      width: state.width,
      height: state.height,
      deviceScaleFactor: state.deviceScaleFactor,
    };

    startTransition(() => {
      captureSnapshot({
        url,
        viewport,
        options: {
          format: "jpeg",
          imageOptions: state.imageOptions,
          block: {
            cookieBanners: true,
            chats: true,
            ads: true,
            trackers: true,
          }
        },
      })
        .then((result) => {
          if (pollControl.current.token !== nextToken) {
            return;
          }

          if (result.success) {
            setState((previous) => ({
              ...previous,
              snapshotId: result.snapshotId,
              status: result.status,
              screenshotUrl: result.screenshotUrl ?? previous.screenshotUrl,
              error: undefined,
            }));

            if (result.status === "COMPLETE") {
              setIsPolling(false);
              return;
            }

            setIsPolling(true);
            schedulePoll(nextToken, result.snapshotId, Date.now());
          } else {
            setIsPolling(false);
            setState((previous) => ({
              ...previous,
              error: result.error,
              snapshotId: undefined,
              status: undefined,
              screenshotUrl: undefined,
            }));
          }
        })
        .catch((error) => {
          if (pollControl.current.token !== nextToken) {
            return;
          }

          setIsPolling(false);
          setState((previous) => ({
            ...previous,
            error: error instanceof Error ? error.message : "Unexpected error",
            snapshotId: undefined,
            status: undefined,
            screenshotUrl: undefined,
          }));
        });
    });
  };

  const statusMessage = (() => {
    if (state.error) {
      return state.error;
    }

    if (isPending) {
      return "Submitting capture request...";
    }

    if (isPolling) {
      return "Processing screenshot, hang tight...";
    }

    if (!isTurnstileReady) {
      return "Complete the Turnstile challenge to unlock the generator.";
    }

    if (state.status === "COMPLETE") {
      return "Screenshot ready.";
    }

    if (state.status === "QUEUED") {
      return "Waiting for the capture to finish...";
    }

    return "Enter a public URL and select a device preset.";
  })();

  const outputWidth = state.width * state.deviceScaleFactor;
  const outputHeight = state.height * state.deviceScaleFactor;

  return (
    <div className="space-y-8">
      <TurnstileChallenge helperText="Complete this step to protect the free tool from abuse." />
      <Card>
        <CardHeader>
          <CardTitle>Configure screenshot</CardTitle>
          <CardDescription>
            {viewportMode === "preset"
              ? "Select a device preset."
              : "Enter custom resolution and DPR."}{" "}
            Output image will be {outputWidth}×{outputHeight}px.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-6"
            onSubmit={(event) => {
              event.preventDefault();
              handleGenerate();
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="screenshot-url">Website URL</Label>
              <Input
                id="screenshot-url"
                inputMode="url"
                placeholder="example.com"
                value={state.url}
                onChange={(event) =>
                  setState((previous) => ({
                    ...previous,
                    url: event.target.value,
                  }))
                }
                disabled={isPending}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>Viewport</Label>
              <Tabs
                value={viewportMode}
                onValueChange={(value) =>
                  setViewportMode(value as "preset" | "custom")
                }
              >
                <TabsList className="w-fit">
                  <TabsTrigger
                    value="preset"
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    Device Preset
                  </TabsTrigger>
                  <TabsTrigger
                    value="custom"
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    Custom
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="preset" className="mt-3">
                  <ViewportSelector
                    options={devicePresets}
                    value={selectedPresetId}
                    onValueChange={(value) => {
                      const preset = devicePresets.find((p) => p.id === value);
                      if (preset) {
                        setSelectedPresetId(preset.id);
                        setState((previous) => ({
                          ...previous,
                          width: preset.width,
                          height: preset.height,
                          deviceScaleFactor: preset.deviceScaleFactor,
                          isMobile: preset.isMobile,
                          hasTouch: preset.hasTouch,
                        }));
                      }
                    }}
                    disabled={isPending}
                    placeholder="Select a device..."
                  />
                </TabsContent>
                <TabsContent value="custom" className="mt-3 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="custom-width">Width (px)</Label>
                      <Input
                        id="custom-width"
                        type="number"
                        min={320}
                        max={3840}
                        value={state.width}
                        onChange={(event) =>
                          setState((previous) => ({
                            ...previous,
                            width: Number(event.target.value),
                          }))
                        }
                        disabled={isPending}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="custom-height">Height (px)</Label>
                      <Input
                        id="custom-height"
                        type="number"
                        min={200}
                        max={2160}
                        value={state.height}
                        onChange={(event) =>
                          setState((previous) => ({
                            ...previous,
                            height: Number(event.target.value),
                          }))
                        }
                        disabled={isPending}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="custom-dpr">DPR</Label>
                      <Input
                        id="custom-dpr"
                        type="number"
                        min={1}
                        max={3}
                        step={0.1}
                        value={state.deviceScaleFactor}
                        onChange={(event) =>
                          setState((previous) => ({
                            ...previous,
                            deviceScaleFactor: Number(event.target.value),
                          }))
                        }
                        disabled={isPending}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Width: 320–3840px, Height: 200–2160px, DPR: 1–3
                  </p>
                </TabsContent>
              </Tabs>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="full-page"
                type="checkbox"
                checked={state.imageOptions.fullPage}
                onChange={(event) =>
                  setState((previous) => ({
                    ...previous,
                    imageOptions: {
                      ...previous.imageOptions,
                      fullPage: event.target.checked,
                    },
                  }))
                }
                className="size-4 rounded border-gray-300"
                disabled={isPending}
              />
              <Label htmlFor="full-page" className="cursor-pointer">
                Capture full page (scroll entire page)
              </Label>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                className="w-full sm:w-auto"
                disabled={isPending || isPolling || !isTurnstileReady}
              >
                {isPending || isPolling ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Generating screenshot...
                  </span>
                ) : (
                  "Generate screenshot"
                )}
              </Button>
              <p className="text-sm text-muted-foreground">{statusMessage}</p>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            Your screenshot will appear below once generated.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div
            className="relative flex min-h-[260px] w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border bg-muted transition-opacity hover:opacity-90"
            onClick={() =>
              state.screenshotUrl &&
              setSelectedImage({
                src: state.screenshotUrl,
                alt: "Website screenshot",
              })
            }
          >
            {state.screenshotUrl ? (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={state.screenshotUrl}
                  alt="Website screenshot"
                  className="h-auto w-full object-contain"
                />
              </>
            ) : (
              <div className="px-6 text-center text-sm text-muted-foreground">
                {isPending || isPolling
                  ? "Hang tight, we are capturing the screenshot..."
                  : "Your generated screenshot will appear here once ready."}
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          {state.screenshotUrl ? (
            <Button variant="outline" asChild>
              <a href={state.screenshotUrl} download>
                <span className="flex items-center gap-2">
                  <Download className="size-4" />
                  Download image
                </span>
              </a>
            </Button>
          ) : (
            <Button variant="outline" disabled>
              <span className="flex items-center gap-2">
                <Download className="size-4" />
                Download image
              </span>
            </Button>
          )}
        </CardFooter>
      </Card>

      {state.screenshotUrl ? (
        <ToolRegistrationCTA
          title="Like this tool?"
          description="Create a free account to save your screenshots and monitor your pages automatically."
        />
      ) : null}

      <ImageModal
        src={selectedImage?.src || ""}
        alt={selectedImage?.alt || ""}
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  );
}
